import { DateTimeStr, forceTz, formatDate } from '@mi-front/core';
import moment from 'moment-timezone';

/** True when the value is a non-empty, parseable datetime string. */
function isValidIso(iso: string | null | undefined): iso is string {
  return iso != null && iso !== '' && moment(iso).isValid();
}

/**
 * Builds a new ISO datetime from a picked calendar date while keeping the existing time-of-day.
 * Returns null when the date is missing/invalid so callers can keep the previous value.
 */
export function combineDateWithExistingTime(
  currentIso: string | null | undefined,
  dateValue: string | null | undefined,
  tz: string,
): string | null {
  if (!dateValue) {
    return null;
  }

  const time = isValidIso(currentIso) ? formatDate(currentIso, 'HH:mm:ss', tz) : '00:00:00';
  const combined = forceTz(`${dateValue} ${time}` as DateTimeStr, tz);
  const next = moment(combined);

  return next.isValid() ? next.toISOString() : null;
}

/**
 * Builds a new ISO datetime from a time input while keeping the existing calendar date.
 * Returns null when the time or base date is missing/invalid so callers can keep the previous value.
 */
export function combineTimeWithExistingDate(
  currentIso: string | null | undefined,
  timeValue: string,
): string | null {
  if (!timeValue || !timeValue.includes(':')) {
    return null;
  }

  const [hour, minutes] = timeValue.split(':');
  if (
    hour === '' ||
    minutes === undefined ||
    minutes === '' ||
    Number.isNaN(Number(hour)) ||
    Number.isNaN(Number(minutes))
  ) {
    return null;
  }

  if (!isValidIso(currentIso)) {
    return null;
  }

  const next = moment(currentIso).clone().hour(Number(hour)).minutes(Number(minutes));
  return next.isValid() ? next.toISOString() : null;
}

/** Safe HH:mm display for a Formik datetime; falls back when the stored value is invalid. */
export function formatTimeInputValue(
  iso: string | null | undefined,
  fallback = '00:00',
  tz?: string,
): string {
  if (!isValidIso(iso)) {
    return fallback;
  }

  return formatDate(iso, 'HH:mm', tz);
}
